import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { AppShell } from "@/components/AppShell";
import { RequireAuth, RequireModule } from "@/components/Guards";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import UsersPage from "@/pages/UsersPage";
import ModulePage from "@/pages/ModulePage";
import Settings from "@/pages/Settings";
import ClientesPage from "@/pages/ClientesPage";
import ClienteDetail from "@/pages/ClienteDetail";
import FazendasPage from "@/pages/FazendasPage";
import FazendaDetail from "@/pages/FazendaDetail";
import EquipamentosPage from "@/pages/EquipamentosPage";
import EquipamentoDetail from "@/pages/EquipamentoDetail";
import OrdensPage from "@/pages/OrdensPage";
import OSFormPage from "@/pages/OSFormPage";
import OSDetailPage from "@/pages/OSDetailPage";
import AgendaPage from "@/pages/AgendaPage";
import DocumentosPage from "@/pages/DocumentosPage";
import ProdutosPage from "@/pages/ProdutosPage";
import ComissoesPage from "@/pages/ComissoesPage";
import SolicitarPage from "@/pages/SolicitarPage";
import FinanceiroPage from "@/pages/FinanceiroPage";
import EstoquePage from "@/pages/EstoquePage";
import { useAuth } from "@/context/AuthContext";

const StaffOnly = ({ children }) => {
  const { user } = useAuth();
  return user.role === "cliente" ? <Navigate to="/ordens-servico" replace /> : children;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  return user.role !== "admin" ? <Navigate to="/ordens-servico" replace /> : children;
};

const PLACEHOLDERS = ["vendas", "fazenda-conectada", "relatorios"];
const STAFF_ROLES = ["admin", "tecnico", "financeiro"];

const guard = (id, el) => <RequireModule id={id}>{el}</RequireModule>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Register />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/redefinir-senha" element={<ResetPassword />} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/clientes" element={guard("clientes", <ClientesPage />)} />
        <Route path="/clientes/:id" element={guard("clientes", <ClienteDetail />)} />
        <Route path="/fazendas" element={guard("fazendas", <FazendasPage />)} />
        <Route path="/fazendas/:id" element={guard("fazendas", <FazendaDetail />)} />
        <Route path="/equipamentos" element={guard("equipamentos", <EquipamentosPage />)} />
        <Route path="/equipamentos/:id" element={guard("equipamentos", <EquipamentoDetail />)} />
        <Route path="/produtos" element={guard("produtos", <ProdutosPage />)} />
        <Route path="/financeiro" element={guard("financeiro", <FinanceiroPage />)} />
        <Route path="/estoque" element={guard("estoque", <EstoquePage />)} />
        <Route path="/comissoes" element={guard("comissoes", <ComissoesPage />)} />
        <Route path="/ordens-servico/solicitar" element={guard("ordens-servico", <SolicitarPage />)} />
        <Route path="/documentos" element={guard("documentos", <DocumentosPage />)} />
        <Route path="/agenda" element={guard("agenda", <AgendaPage />)} />
        <Route path="/ordens-servico" element={guard("ordens-servico", <OrdensPage />)} />
        <Route path="/ordens-servico/nova" element={guard("ordens-servico", <AdminOnly><OSFormPage /></AdminOnly>)} />
        <Route path="/ordens-servico/:id" element={guard("ordens-servico", <OSDetailPage />)} />
        <Route path="/ordens-servico/:id/editar" element={guard("ordens-servico", <StaffOnly><OSFormPage /></StaffOnly>)} />
        <Route path="/colaboradores" element={guard("colaboradores", <UsersPage testId="colaboradores-page" title="Colaboradores" subtitle="Administradores, técnicos e equipe financeira." roles={STAFF_ROLES} />)} />
        <Route path="/configuracoes" element={<Settings />} />
        {PLACEHOLDERS.map((id) => <Route key={id} path={`/${id}`} element={guard(id, <ModulePage id={id} />)} />)}
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
